import { useState } from 'react'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Fuel,
  ListChecks,
  Pencil,
  Plus,
  Trash2,
  Undo2,
} from 'lucide-react'

import { Field } from '../../components/Field'
import { useRoadbook } from '../../data/roadbookContext'
import { emptyKitItem, emptyVignette } from '../../lib/checks'
import type { KitCheck, VignetteCheck } from '../../lib/types'
import { patchAt } from '../stages/stageLists'
import { useListEditing, type ListEditing } from './listEditing'

/**
 * Die beiden Checklisten: Vignetten und Ersatzteile.
 *
 * Beide sind ergänzbar, denn die Reise weicht von der Planung ab. Fahren sie
 * spontan durch ein Land, das nicht auf der Liste steht, muss dessen Vignette
 * eintragbar sein - sonst ist die Liste an genau dem Tag falsch, an dem sie
 * gebraucht wird.
 *
 * Abhaken bleibt der Normalfall und ist deshalb die Ansicht, die zuerst da ist.
 * Das Bearbeiten liegt hinter einem Knopf: acht Vignetten mit je drei Feldern
 * dauerhaft offen wären eine Wand aus Eingabefeldern für einen Handgriff, der
 * ein paar Mal pro Reise vorkommt. Und gedruckt wird die Liste zum Abhaken, nicht
 * das Formular.
 *
 * Die Listenoperationen und die stabilen React-Schlüssel kommen unverändert von
 * den Etappeninhalten (../stages/stageLists.ts, ../stages/rowKeys.ts): dieselbe
 * Aufgabe, dort gelöst und geprüft.
 */
export function ChecklistView() {
  const { checks, setChecks, darfSchreiben } = useRoadbook()

  return (
    <div className="zwei-spalten">
      <VignettenBlock
        werte={checks.vignettes}
        nurLesen={!darfSchreiben}
        onChange={(vignettes) => setChecks({ vignettes })}
      />
      <KitBlock
        werte={checks.kit}
        nurLesen={!darfSchreiben}
        onChange={(kit) => setChecks({ kit })}
      />
    </div>
  )
}

function VignettenBlock({
  werte,
  nurLesen,
  onChange,
}: {
  werte: VignetteCheck[]
  nurLesen: boolean
  onChange: (next: VignetteCheck[]) => void
}) {
  const liste = useListEditing(werte, onChange)
  const [bearbeiten, setBearbeiten] = useState(false)

  return (
    <div className="block">
      <h4>
        <Fuel size={14} /> Vignetten und Grenzen
        <span className="fortschritt">
          {werte.filter((v) => v.done).length}/{werte.length}
        </span>
      </h4>

      {werte.length === 0 && <p className="leer">Keine Vignette eingetragen.</p>}

      {!bearbeiten && (
        <ul className="checkliste">
          {werte.map((v, i) => (
            <li key={liste.keys.at(i)}>
              <label>
                {/* Abhaken ist auch Schreiben: ohne Recht bleibt der Haken, wo
                    er ist, statt aufzuspringen und beim Neuladen zurückzufallen. */}
                <input
                  type="checkbox"
                  checked={v.done}
                  disabled={nurLesen}
                  onChange={(e) => onChange(patchAt(werte, i, { done: e.target.checked }))}
                />
                <span>
                  <b>{v.country || 'Ohne Namen'}</b> <span className="preis">{v.price}</span>
                  {v.note && (
                    <>
                      <br />
                      <small>{v.note}</small>
                    </>
                  )}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {bearbeiten && (
        <section className="inhalt-block kein-druck">
          <ul className="eintragsliste">
            {werte.map((v, i) => (
              <li key={liste.keys.at(i)} className="eintrag">
                <span className="eintrag-nummer">Vignette {i + 1}</span>

                <Field label="Land">
                  <input
                    value={v.country}
                    autoComplete="off"
                    placeholder="z. B. Nordmazedonien"
                    onChange={(e) => onChange(patchAt(werte, i, { country: e.target.value }))}
                  />
                </Field>

                <div className="raster-2">
                  <Field label="Preis">
                    {/* Kein numerisches Feld: hier steht "16 € (7 Tage)" oder "—". */}
                    <input
                      value={v.price}
                      autoComplete="off"
                      placeholder="z. B. 12 € (10 Tage)"
                      onChange={(e) => onChange(patchAt(werte, i, { price: e.target.value }))}
                    />
                  </Field>
                  <Field label="Notiz">
                    <input
                      value={v.note}
                      autoComplete="off"
                      placeholder="Wo kaufen, was gilt"
                      onChange={(e) => onChange(patchAt(werte, i, { note: e.target.value }))}
                    />
                  </Field>
                </div>

                <ZeilenKnoepfe liste={liste} index={i} anzahl={werte.length} einzahl="Vignette" />
              </li>
            ))}
          </ul>

          <NeuerEintrag
            liste={liste}
            beschriftung="Vignette hinzufügen"
            leer={emptyVignette}
            name={(v) => v.country}
          />
        </section>
      )}

      {!nurLesen && (
        <BearbeitenKnopf
          bearbeiten={bearbeiten}
          beschriftung="Vignetten bearbeiten"
          onToggle={() => {
            setBearbeiten(!bearbeiten)
            liste.vergessen()
          }}
        />
      )}
    </div>
  )
}

function KitBlock({
  werte,
  nurLesen,
  onChange,
}: {
  werte: KitCheck[]
  nurLesen: boolean
  onChange: (next: KitCheck[]) => void
}) {
  const liste = useListEditing(werte, onChange)
  const [bearbeiten, setBearbeiten] = useState(false)

  return (
    <div className="block">
      <h4>
        <ListChecks size={14} /> Ersatzteile und Papiere
        <span className="fortschritt">
          {werte.filter((k) => k.done).length}/{werte.length}
        </span>
      </h4>

      {werte.length === 0 && <p className="leer">Nichts eingetragen.</p>}

      {!bearbeiten && (
        <ul className="checkliste">
          {werte.map((item, i) => (
            <li key={liste.keys.at(i)}>
              <label>
                <input
                  type="checkbox"
                  checked={item.done}
                  disabled={nurLesen}
                  onChange={(e) => onChange(patchAt(werte, i, { done: e.target.checked }))}
                />
                <span>{item.name || 'Ohne Namen'}</span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {bearbeiten && (
        <section className="inhalt-block kein-druck">
          <ul className="eintragsliste">
            {werte.map((item, i) => (
              <li key={liste.keys.at(i)} className="eintrag">
                <Field label={`Eintrag ${i + 1}`}>
                  <input
                    value={item.name}
                    autoComplete="off"
                    placeholder="z. B. Zündkerzen"
                    onChange={(e) => onChange(patchAt(werte, i, { name: e.target.value }))}
                  />
                </Field>

                <ZeilenKnoepfe liste={liste} index={i} anzahl={werte.length} einzahl="Eintrag" />
              </li>
            ))}
          </ul>

          <NeuerEintrag
            liste={liste}
            beschriftung="Eintrag hinzufügen"
            leer={emptyKitItem}
            name={(k) => k.name}
          />
        </section>
      )}

      {!nurLesen && (
        <BearbeitenKnopf
          bearbeiten={bearbeiten}
          beschriftung="Liste bearbeiten"
          onToggle={() => {
            setBearbeiten(!bearbeiten)
            liste.vergessen()
          }}
        />
      )}

      <p className="hinweis">
        <AlertTriangle size={13} /> Kühlsystem vor Abfahrt servicen: Lüfter, Thermoschalter, Riemen,
        Schläuche. Überhitzung ist bei Hitze und Pässen das Hauptrisiko.
      </p>
    </div>
  )
}

/** Verschieben und entfernen einer Zeile. Wortbeschriftung, damit es im Stehen trifft. */
function ZeilenKnoepfe<T>({
  liste,
  index,
  anzahl,
  einzahl,
}: {
  liste: ListEditing<T>
  index: number
  anzahl: number
  einzahl: string
}) {
  return (
    <div className="eintrag-knoepfe">
      <button
        type="button"
        className="knopf still"
        disabled={index === 0}
        onClick={() => liste.verschieben(index, -1)}
        aria-label={`${einzahl} ${index + 1} nach oben`}
      >
        <ChevronUp size={14} /> Höher
      </button>
      <button
        type="button"
        className="knopf still"
        disabled={index === anzahl - 1}
        onClick={() => liste.verschieben(index, 1)}
        aria-label={`${einzahl} ${index + 1} nach unten`}
      >
        <ChevronDown size={14} /> Tiefer
      </button>
      <button
        type="button"
        className="knopf gefahr"
        onClick={() => liste.entfernen(index)}
        aria-label={`${einzahl} ${index + 1} entfernen`}
      >
        <Trash2 size={14} /> Entfernen
      </button>
    </div>
  )
}

/** Hinzufügen, und daneben das Zurückholen des zuletzt Entfernten. */
function NeuerEintrag<T>({
  liste,
  beschriftung,
  leer,
  name,
}: {
  liste: ListEditing<T>
  beschriftung: string
  leer: () => T
  name: (eintrag: T) => string
}) {
  return (
    <div className="knopfreihe">
      <button type="button" className="knopf" onClick={() => liste.anhaengen(leer())}>
        <Plus size={14} /> {beschriftung}
      </button>
      {liste.zurueck && (
        <button type="button" className="knopf still" onClick={liste.zurueckholen}>
          <Undo2 size={14} /> {kurz(name(liste.zurueck))} zurückholen
        </button>
      )}
    </div>
  )
}

/** Der Umschalter zwischen Abhaken und Bearbeiten. Auf Papier hat er nichts zu suchen. */
function BearbeitenKnopf({
  bearbeiten,
  beschriftung,
  onToggle,
}: {
  bearbeiten: boolean
  beschriftung: string
  onToggle: () => void
}) {
  return (
    <div className="knopfreihe kein-druck">
      <button type="button" className="knopf still" onClick={onToggle}>
        {bearbeiten ? (
          <>
            <Check size={14} /> Fertig
          </>
        ) : (
          <>
            <Pencil size={14} /> {beschriftung}
          </>
        )}
      </button>
    </div>
  )
}

/** Kurzfassung eines Eintrags für den Zurückholen-Knopf. */
function kurz(text: string): string {
  const sauber = text.trim()
  if (sauber === '') return 'Leeren Eintrag'
  return `„${sauber.length > 24 ? `${sauber.slice(0, 24)}…` : sauber}“`
}
